import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useSEO from 'hooks/useSEO'
import LPNavigation from '../landing/sections/LPNavigation'
import CTABanner from '../landing/sections/CTABanner'
import LPFooter from '../landing/sections/LPFooter'

// ── Feature row value renderer ────────────────────────────────────────────────
function Val({ v }) {
  if (v === true)      return <span className="text-[#1B5E20] font-bold text-base">✓</span>
  if (v === false)     return <span className="text-[#CBD5E1] text-base">—</span>
  if (v === 'partial') return <span className="text-[#F59E0B] text-sm font-medium">Partial</span>
  return <span className="text-sm text-[#475569]">{v}</span>
}

const FEATURES = [
  { label: 'Free plan',                   key: 'free',       sdt: true },
  { label: 'Starting price',             key: 'price',      sdt: 'Free forever' },
  { label: 'Dividend tracking',          key: 'divTrack',   sdt: true },
  { label: 'DRIP snowball projections',  key: 'drip',       sdt: true },
  { label: 'Dividend calendar',          key: 'calendar',   sdt: true },
  { label: 'Covered call tracker',       key: 'cc',         sdt: true,  sdtNote: 'Unique to SDT' },
  { label: 'Cash-secured put tracker',   key: 'csp',        sdt: true,  sdtNote: 'Unique to SDT' },
  { label: 'Wheel strategy tracker',     key: 'wheel',      sdt: true,  sdtNote: 'Unique to SDT' },
  { label: 'Brokerage sync',             key: 'sync',       sdt: 'Premium' },
  { label: 'iOS & Android apps',         key: 'mobile',     sdt: true },
  { label: 'Income reports & CSV export',key: 'reports',    sdt: true },
  { label: 'Webull CSV import',          key: 'webull',     sdt: true },
]

export default function ComparisonTemplate({ competitor }) {
  const {
    name, slug, tagline, category,
    seoTitle, seoDescription,
    price, priceNote,
    features,           // object keyed by FEATURES key
    strengths,          // array of strings — what the competitor genuinely does well
    verdict,            // { sdtWins: [...], competitorWins: [...], bottomLine: string }
    bestFor,            // { sdt: string, competitor: string }
    faq,                // array of {q, a}
  } = competitor

  useSEO({
    title: seoTitle,
    description: seoDescription,
    canonical: `/vs-${slug}`,
  })

  useEffect(() => {
    const id = `vs-${slug}-jsonld`
    if (document.getElementById(id)) return
    const s = document.createElement('script')
    s.id = id
    s.type = 'application/ld+json'
    s.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: seoTitle,
      url: `https://safedividendtracker.com/vs-${slug}`,
      description: seoDescription,
    })
    document.head.appendChild(s)
    return () => document.getElementById(id)?.remove()
  }, [slug, seoTitle, seoDescription])

  const [openFaq, setOpenFaq] = useState(null)

  return (
    <div className="min-h-screen bg-white">
      <LPNavigation />

      {/* Hero */}
      <section className="pt-28 pb-14 px-4 bg-gradient-to-b from-[#F0FDF4] to-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4">Comparison</p>
          <h1 className="font-display text-5xl lg:text-6xl text-[#0F172A] leading-tight">
            Safe Dividend Tracker<br className="hidden sm:block" />
            <span className="text-[#94A3B8]"> vs </span>{name}
          </h1>
          <p className="mt-5 text-lg text-[#475569] max-w-2xl mx-auto">
            {tagline}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <a
              href="/register"
              className="inline-block bg-[#1B5E20] text-white font-medium text-base px-8 py-4 rounded-full hover:bg-[#145218] hover:-translate-y-px transition-all duration-300 shadow-sm"
            >
              Try Safe Dividend Tracker Free
            </a>
            <a
              href={`https://${slug}.com`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-[#475569] font-medium text-base px-8 py-4 rounded-full border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-all duration-300"
            >
              Visit {name} →
            </a>
          </div>
          <p className="mt-4 text-xs text-[#94A3B8] tracking-wide uppercase">Free plan forever · No credit card required</p>
        </div>
      </section>

      {/* Quick verdict cards */}
      <section className="px-4 pb-14">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-4">
          {verdict.sdtWins.map((win, i) => (
            <div key={i} className="bg-[#F0FDF4] border border-[#DCFCE7] rounded-xl p-5">
              <p className="text-xs font-medium text-[#1B5E20] uppercase tracking-wide mb-2">SDT wins</p>
              <p className="text-sm font-semibold text-[#0F172A]">{win}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature comparison table */}
      <section className="bg-[#F8FAFC] px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">Side by side</p>
          <h2 className="font-display text-4xl text-[#0F172A] text-center mb-10">
            Feature comparison
          </h2>
          <div className="overflow-hidden border border-[#E2E8F0] rounded-2xl shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0F172A]">
                  <th className="px-5 py-4 text-left text-xs font-medium text-[#94A3B8] w-1/2">Feature</th>
                  <th className="px-5 py-4 text-center text-xs font-medium text-[#22C55E]">
                    Safe Dividend Tracker
                  </th>
                  <th className="px-5 py-4 text-center text-xs font-medium text-[#94A3B8]">
                    {name}
                  </th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map(({ label, key, sdt, sdtNote }, i) => {
                  const competitorVal = key === 'price' ? price : (features[key] ?? false)
                  const sdtVal = key === 'price' ? sdt : sdt
                  const isSDTUnique = sdtNote && features[key] === false
                  return (
                    <tr key={key} className={`border-b border-[#F1F5F9] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
                      <td className="px-5 py-3.5 text-[#0F172A]">
                        {label}
                        {isSDTUnique && (
                          <span className="ml-2 text-xs text-[#1B5E20] bg-[#DCFCE7] px-1.5 py-0.5 rounded-full font-medium">
                            SDT only
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {key === 'price' ? (
                          <span className="text-sm font-semibold text-[#1B5E20]">{sdt}</span>
                        ) : (
                          <Val v={sdtVal} />
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <Val v={competitorVal} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {priceNote && (
            <p className="text-xs text-[#94A3B8] mt-3 text-center">{priceNote}</p>
          )}
        </div>
      </section>

      {/* Competitor strengths — honest section */}
      <section className="bg-white px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">Being fair</p>
          <h2 className="font-display text-4xl text-[#0F172A] text-center mb-3">
            Where {name} excels
          </h2>
          <p className="text-center text-[#475569] mb-10">
            We believe in honest comparisons. Here's what {name} genuinely does well.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {strengths.map((s, i) => (
              <div key={i} className="flex gap-3 p-4 border border-[#E2E8F0] rounded-xl">
                <span className="text-[#F59E0B] font-bold mt-0.5">★</span>
                <p className="text-sm text-[#475569] leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's best for */}
      <section className="bg-[#F8FAFC] px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">Bottom line</p>
          <h2 className="font-display text-4xl text-[#0F172A] text-center mb-10">Who should use which?</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-[#1B5E20] rounded-2xl p-6 text-white">
              <p className="text-xs font-medium text-[#A7F3D0] uppercase tracking-wide mb-3">Choose Safe Dividend Tracker if…</p>
              <p className="text-sm leading-relaxed text-[#DCFCE7]">{bestFor.sdt}</p>
              <a
                href="/register"
                className="inline-block mt-5 bg-white text-[#1B5E20] font-medium text-sm px-5 py-2.5 rounded-full hover:bg-[#F0FDF4] transition-colors"
              >
                Get Started Free →
              </a>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
              <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wide mb-3">Choose {name} if…</p>
              <p className="text-sm leading-relaxed text-[#475569]">{bestFor.competitor}</p>
              <a
                href={`https://${slug}.com`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-5 text-[#475569] text-sm font-medium border border-[#E2E8F0] px-5 py-2.5 rounded-full hover:bg-[#F8FAFC] transition-colors"
              >
                Visit {name} →
              </a>
            </div>
          </div>
          <div className="mt-8 bg-white border border-[#DCFCE7] rounded-xl p-5">
            <p className="text-sm text-[#0F172A] leading-relaxed"><strong>Our take:</strong> {verdict.bottomLine}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq?.length > 0 && (
        <section className="bg-white px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">FAQs</p>
            <h2 className="font-display text-4xl text-[#0F172A] text-center mb-10">
              Common questions
            </h2>
            {faq.map((item, i) => (
              <div key={i} className="border-b border-[#E2E8F0]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left group"
                >
                  <span className="text-sm font-medium text-[#0F172A] pr-8 group-hover:text-[#1B5E20] transition-colors">
                    {item.q}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                    className={`shrink-0 text-[#1B5E20] transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}>
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-out"
                  style={{ maxHeight: openFaq === i ? '250px' : '0', opacity: openFaq === i ? 1 : 0 }}
                >
                  <p className="text-sm text-[#475569] leading-relaxed pb-5">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <CTABanner
        headline={`Switch from ${name} — it's free`}
        subtext="Import your portfolio in minutes. No credit card, no commitment."
        ctaText="Start Free — No Credit Card"
        microcopy="Free plan forever · No credit card required"
      />
      <LPFooter />
    </div>
  )
}
